import React, { useState } from 'react';
import { Newspaper, Trophy, Camera, ClipboardList, Users, Settings, LogOut, ExternalLink, Handshake, Image, Menu, X, FileText, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import NewsManager from './NewsManager';
import ResultsManager from './ResultsManager';
import GalasManager from './GalasManager';
import RegistrationManager from './RegistrationManager';
import ClubManager from './ClubManager';
import SettingsManager from './SettingsManager';
import PartnersManager from './PartnersManager';
import MediaManager from './MediaManager';
import ContentManager from './ContentManager';
import ScheduleManager from './ScheduleManager';

const tabs = [
  { id: 'news', label: 'Actualités', icon: Newspaper, comp: NewsManager },
  { id: 'results', label: 'Résultats', icon: Trophy, comp: ResultsManager },
  { id: 'galas', label: 'Galas', icon: Camera, comp: GalasManager },
  { id: 'registration', label: 'Inscriptions', icon: ClipboardList, comp: RegistrationManager },
  { id: 'schedule', label: 'Planning cours', icon: Calendar, comp: ScheduleManager },
  { id: 'club', label: 'Notre Club', icon: Users, comp: ClubManager },
  { id: 'content', label: 'Contenu', icon: FileText, comp: ContentManager },
  { id: 'partners', label: 'Partenaires', icon: Handshake, comp: PartnersManager },
  { id: 'media', label: 'Images des pages', icon: Image, comp: MediaManager },
  { id: 'settings', label: 'Paramètres', icon: Settings, comp: SettingsManager },
];

const AdminDashboard = () => {
  const [active, setActive] = useState('news');
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const ActiveComp = tabs.find((t) => t.id === active).comp;
  const activeLabel = tabs.find((t) => t.id === active).label;

  const closeAndSet = (id) => { setActive(id); setMenuOpen(false); };

  const sidebar = (
    <>
      <div className="p-6 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/images/logo.png" alt="Logo" className="h-10 w-auto" />
          <div>
            <h1 className="text-white font-bold text-sm">GR La Saline</h1>
            <p className="text-white/40 text-xs">Administration</p>
          </div>
        </div>
        <button onClick={() => setMenuOpen(false)} className="lg:hidden text-white/60 hover:text-white" data-testid="close-menu-btn"><X size={20} /></button>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => closeAndSet(t.id)}
            data-testid={`tab-${t.id}`}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              active === t.id ? 'bg-gradient-to-r from-fuchsia-500/20 to-pink-500/20 text-fuchsia-300 border border-fuchsia-500/40' : 'text-white/60 hover:bg-white/5'
            }`}
          >
            <t.icon size={18} /> {t.label}
          </button>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10 space-y-1">
        <a href="/" target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/60 hover:bg-white/5">
          <ExternalLink size={18} /> Voir le site
        </a>
        <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/60 hover:bg-red-500/10 hover:text-red-400">
          <LogOut size={18} /> Déconnexion
        </button>
        <p className="text-white/30 text-xs px-3 pt-2 truncate">{user?.sub || user?.email}</p>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-black">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-neutral-950/95 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => setMenuOpen(true)} className="text-white p-2" data-testid="open-menu-btn"><Menu size={22} /></button>
        <img src="/images/logo.png" alt="" className="h-8 w-auto" />
        <span className="text-white font-semibold text-sm">{activeLabel}</span>
      </header>

      {/* Sidebar overlay for mobile */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-neutral-950 border-r border-white/10 flex flex-col h-full">
            {sidebar}
          </div>
          <div className="flex-1 bg-black/70" onClick={() => setMenuOpen(false)} />
        </div>
      )}

      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-60 xl:w-64 bg-neutral-950 border-r border-white/10 flex-col fixed h-full">
        {sidebar}
      </aside>

      {/* Main */}
      <main className="lg:ml-60 xl:ml-64 pt-16 lg:pt-0 p-4 md:p-8 xl:p-10">
        <ActiveComp />
      </main>
    </div>
  );
};

export default AdminDashboard;
